import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDeals } from '../../context/DataContext';
import { useInvoices, calcTotals } from '../../context/InvoicesContext';
import { api } from '../../lib/api';
import { fmtMoney, fmtDate, fmtDateFull } from '../../lib/format';
import { ChevronDown, ChevronRight, Plus, Trash2, ExternalLink, FileText, Zap, MessageSquare, AtSign, Send, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { NumberInput } from '../ui/number-input';
import { DEAL_INVOICE_STATUSES, dealInvoiceStatusMeta, isDealInvoiceStatusDone, dealTalentIds } from '../../lib/deals';
import InitialsAvatar from '../InitialsAvatar';
import { toast } from 'sonner';

const InvHead = () => (
  <thead className="bg-slate-50 text-slate-500">
    <tr>
      <th className="w-8"></th>
      <th className="text-left font-medium px-3 py-3">Deal Name</th>
      <th className="text-left font-medium px-3 py-3">Fee</th>
      <th className="text-left font-medium px-3 py-3">Commission</th>
      <th className="text-left font-medium px-3 py-3">Net Fee</th>
      <th className="text-left font-medium px-3 py-3">PO Number</th>
      <th className="text-left font-medium px-3 py-3"><span className="inline-flex items-center gap-1"><Zap className="w-3 h-3 text-pink-500" />Split Invoices</span></th>
      <th className="text-left font-medium px-3 py-3">Invoice Status</th>
      <th className="text-left font-medium px-3 py-3">Invoice Due</th>
      <th className="text-left font-medium px-3 py-3">Generate</th>
      <th className="text-center font-medium px-3 py-3">Invoiced</th>
      <th className="text-center font-medium px-3 py-3">Paid In</th>
      <th className="text-center font-medium px-3 py-3">Paid Out</th>
      <th className="text-center font-medium px-3 py-3">Notes</th>
    </tr>
  </thead>
);

const noteId = () => 'note_' + (window.crypto?.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 6));
const todayISO = () => new Date().toISOString().slice(0, 10);

// Invoiced / paid-in / paid-out tick — same deal fields as the deal dialog, so toggling here
// updates the deal everywhere.
const PaymentCheck = ({ deal, field, color, title, canEdit, onUpdate }) => (
  <input
    type="checkbox"
    checked={Boolean(deal[field])}
    disabled={!canEdit}
    onChange={(e) => onUpdate({ [field]: e.target.checked ? (deal[field] || todayISO()) : '' })}
    style={{ accentColor: color }}
    className={`w-4 h-4 ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
    title={deal[field] ? `${title} · ${deal[field]}` : `mark ${title}`}
    data-testid={`pay-${field}-${deal.id}`}
  />
);
const fmtNoteTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Notes / messages for the talent on a deal's invoice row. Managers write & tag talent; talent read-only.
const InvoiceNotesCell = ({ deal, talentMap, canEdit, onUpdate }) => {
  const notes = deal.invoice_notes || [];
  const talentIds = dealTalentIds(deal);
  const [draft, setDraft] = useState('');
  const [tagged, setTagged] = useState([]);

  const addNote = () => {
    const text = draft.trim();
    if (!text) return;
    const next = [...notes, { id: noteId(), text, mentions: tagged, at: new Date().toISOString() }];
    onUpdate({ invoice_notes: next });
    setDraft('');
    setTagged([]);
  };
  const removeNote = (id) => onUpdate({ invoice_notes: notes.filter((n) => n.id !== id) });
  const toggleTag = (id) => setTagged((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`relative inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${notes.length ? 'border-pink-300 text-pink-600 bg-pink-50 hover:bg-pink-100' : 'border-slate-200 text-slate-400 hover:text-pink-600 hover:border-pink-300'}`}
          title={notes.length ? `${notes.length} note${notes.length === 1 ? '' : 's'}` : 'add a note for the talent'}
          data-testid={`invoice-notes-trigger-${deal.id}`}
        >
          <MessageSquare className="w-4 h-4" />
          {notes.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-pink-500 text-white text-[10px] font-semibold inline-flex items-center justify-center">{notes.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" data-testid={`invoice-notes-popover-${deal.id}`}>
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">notes for talent</div>
        <div className="max-h-56 overflow-y-auto space-y-2 mb-3">
          {notes.length === 0 && <div className="text-xs text-slate-400 italic">no notes yet</div>}
          {notes.map((n) => (
            <div key={n.id} className="group rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-2" data-testid={`invoice-note-${n.id}`}>
              <div className="flex items-start gap-2">
                <p className="flex-1 text-xs text-slate-700 whitespace-pre-wrap break-words">{n.text}</p>
                {canEdit && (
                  <button onClick={() => removeNote(n.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600 shrink-0" title="delete note">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {(n.mentions || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {(n.mentions || []).map((id) => (
                    <span key={id} className="inline-flex items-center gap-0.5 text-[10px] font-medium text-pink-700 bg-pink-100 rounded-full px-1.5 py-0.5">
                      <AtSign className="w-2.5 h-2.5" />{talentMap?.[id]?.name || 'talent'}
                    </span>
                  ))}
                </div>
              )}
              {n.at && <div className="text-[10px] text-slate-400 mt-1">{fmtNoteTime(n.at)}</div>}
            </div>
          ))}
        </div>
        {canEdit ? (
          <div className="border-t border-slate-100 pt-2.5">
            {talentIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {talentIds.map((id) => (
                  <button
                    key={id}
                    onClick={() => toggleTag(id)}
                    className={`inline-flex items-center gap-0.5 text-[10px] font-medium rounded-full px-1.5 py-0.5 border transition-colors ${tagged.includes(id) ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                    data-testid={`invoice-note-tag-${deal.id}-${id}`}
                  >
                    <AtSign className="w-2.5 h-2.5" />{talentMap?.[id]?.name || 'talent'}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-1.5">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }}
                rows={2}
                placeholder="message for the talent…"
                className="flex-1 text-xs px-2 py-1.5 border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-pink-200 resize-none"
                data-testid={`invoice-note-input-${deal.id}`}
              />
              <button
                onClick={addNote}
                disabled={!draft.trim()}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-40 shrink-0"
                data-testid={`invoice-note-add-${deal.id}`}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-[10px] text-slate-400 border-t border-slate-100 pt-2">read only</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const StatusChip = ({ status }) => {
  if (status === 'invoiced') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-mint-100 text-mint-700">
        <span className="w-1.5 h-1.5 rounded-full bg-mint-500" />
        Invoiced
      </span>
    );
  }
  const meta = dealInvoiceStatusMeta(status);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${meta.color}1f`, color: meta.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
};

// Editable status dropdown shown when a deal has no real generated invoice yet — lets a
// manager track its lifecycle (awaiting PO, invoiced elsewhere, overdue, paid out…) without
// necessarily generating an invoice here.
const InvoiceStatusSelect = ({ deal, value: valueOverride, onUpdate }) => {
  const value = valueOverride || deal.invoice_status || 'to_invoice';
  const meta = dealInvoiceStatusMeta(value);
  return (
    <Select value={value} onValueChange={(v) => onUpdate({ invoice_status: v })}>
      <SelectTrigger
        className="h-7 px-2 py-0 w-auto min-w-[150px] text-[11px] font-medium border-0 rounded-full [&>svg]:opacity-60"
        style={{ background: `${meta.color}22`, color: meta.color }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`invoice-status-select-${deal.id}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent onClick={(e) => e.stopPropagation()}>
        {DEAL_INVOICE_STATUSES.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
              <span style={{ color: s.color }}>{s.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const emptySplit = (idx = 1) => ({
  id: 'sp_' + Math.random().toString(36).slice(2, 10),
  description: `Invoice ${idx}`,
  amount: 0,
  commission: 0,
  invoice_date: '',
  sent_date: '',
  terms_days: 30,
  status: 'to_invoice',
});

const addDays = (isoDate, days) => {
  if (!isoDate) return null;
  try {
    const d = new Date(isoDate + 'T00:00:00');
    d.setDate(d.getDate() + Number(days || 0));
    return d.toISOString().slice(0, 10);
  } catch { return null; }
};

/**
 * DealSplitRow — child row shown when a deal has split_invoicing enabled
 */
const DealSplitRow = ({ deal, split, invoice, onPatch, onRemove, onGenerate, onDeleteInvoice, canEdit }) => {
  const due = split.sent_date ? addDays(split.sent_date, split.terms_days) : (split.invoice_date ? addDays(split.invoice_date, split.terms_days) : null);
  const invoiced = split.status === 'invoiced' || !!split.invoice_id;
  return (
    <tr className="bg-lilac-50/30 border-t border-lilac-100" data-testid={`split-row-${split.id}`}>
      <td className="px-2 py-2"></td>
      <td className="px-3 py-2">
        <Input
          value={split.description || ''}
          onChange={(e) => onPatch({ description: e.target.value })}
          className="h-8 text-sm bg-white"
          disabled={!canEdit || invoiced}
          placeholder="description"
        />
      </td>
      <td className="px-3 py-2">
        <NumberInput
          value={split.amount}
          onChange={(v) => onPatch({ amount: Number(v) || 0 })}
          className="h-8 text-sm bg-white w-28"
          disabled={!canEdit || invoiced}
          data-testid={`split-amount-${split.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="date"
          value={split.invoice_date || ''}
          onChange={(e) => onPatch({ invoice_date: e.target.value })}
          className="h-8 text-sm bg-white w-36"
          disabled={!canEdit || invoiced}
        />
      </td>
      <td className="px-3 py-2">
        {invoiced && invoice ? (
          <span className="inline-flex items-center gap-1.5">
            <Link to={`/invoicing/${invoice.id}`} className="text-xs text-pink-600 hover:text-pink-700 inline-flex items-center gap-1 font-medium">
              <FileText className="w-3.5 h-3.5" /> {invoice.number}
            </Link>
            {canEdit && (
              <button onClick={onDeleteInvoice} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="delete this invoice" data-testid={`delete-split-invoice-${split.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        ) : (
          <Button size="sm" onClick={onGenerate} disabled={!canEdit || (Number(split.amount) || 0) <= 0} className="h-7 px-2.5 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid={`generate-split-${split.id}`}>
            Generate
          </Button>
        )}
      </td>
      <td className="px-3 py-2">
        <Input
          type="date"
          value={split.sent_date || ''}
          onChange={(e) => onPatch({ sent_date: e.target.value })}
          className="h-8 text-sm bg-white w-36"
          disabled={!canEdit}
        />
      </td>
      <td className="px-3 py-2 text-slate-600">
        <div className="flex items-center gap-1">
          <NumberInput
            value={split.terms_days}
            onChange={(v) => onPatch({ terms_days: Number(v) || 0 })}
            className="h-8 text-sm bg-white w-16"
            disabled={!canEdit || invoiced}
          />
          <span className="text-xs text-slate-500">days</span>
        </div>
      </td>
      <td className="px-3 py-2">
        <Input
          type="date"
          value={split.due_date || due || ''}
          onChange={(e) => onPatch({ due_date: e.target.value })}
          className="h-8 text-xs bg-white w-36"
          disabled={!canEdit}
          data-testid={`split-due-${split.id}`}
        />
      </td>
      <td className="px-3 py-2">
        {canEdit ? (
          <Select value={split.status || 'to_invoice'} onValueChange={(v) => onPatch({ status: v })}>
            <SelectTrigger
              className="h-7 px-2 py-0 w-auto min-w-[140px] text-[11px] font-medium border-0 rounded-full [&>svg]:opacity-60"
              style={{ background: `${dealInvoiceStatusMeta(split.status || 'to_invoice').color}22`, color: dealInvoiceStatusMeta(split.status || 'to_invoice').color }}
              onClick={(e) => e.stopPropagation()}
              data-testid={`split-status-select-${split.id}`}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dealInvoiceStatusMeta(split.status || 'to_invoice').color }} />
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent onClick={(e) => e.stopPropagation()}>
              {DEAL_INVOICE_STATUSES.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                    <span style={{ color: s.color }}>{s.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <StatusChip status={split.status || 'to_invoice'} />
        )}
      </td>
      <td className="px-2 py-2 text-right">
        {canEdit && !invoiced && (
          <button onClick={onRemove} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="remove split">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
};

/**
 * DealInvoicingRow — top-level row per deal
 */
const DealInvoicingRow = ({ deal, invoices, brandMap, talentMap, canEdit, hideAddSplit = false, onUpdate, onGenerated, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const brand = brandMap[deal.brand_id];
  const fee = Number(deal.value) || 0;
  const commissionPct = Number(deal.commission_percent) || 0;
  const commission = fee * (commissionPct / 100);
  const netFee = fee - commission;

  // Single (no-split) invoice for this deal — first invoice tied to deal_id where the split's invoice_id doesn't already claim it
  const dealInvoices = invoices.filter((inv) => inv.deal_id === deal.id);
  const splitIds = new Set((deal.invoice_splits || []).map((s) => s.invoice_id).filter(Boolean));
  const singleInv = dealInvoices.find((inv) => !splitIds.has(inv.id));
  const invoicedAll = deal.split_invoicing
    ? ((deal.invoice_splits || []).length > 0 && (deal.invoice_splits || []).every((s) => s.status === 'invoiced'))
    : Boolean(singleInv);

  const patchSplits = (splits) => onUpdate({ invoice_splits: splits });
  const setSplit = (idx, patch) => patchSplits((deal.invoice_splits || []).map((s, i) => i === idx ? { ...s, ...patch } : s));
  const addSplit = () => {
    const splits = deal.invoice_splits || [];
    const usedAmount = splits.reduce((a, s) => a + (Number(s.amount) || 0), 0);
    const next = { ...emptySplit(splits.length + 1), amount: Math.max(0, fee - usedAmount) };
    patchSplits([...splits, next]);
  };
  const removeSplit = (idx) => patchSplits((deal.invoice_splits || []).filter((_, i) => i !== idx));

  const toggleSplit = (v) => {
    const patch = { split_invoicing: v };
    if (v && (deal.invoice_splits || []).length === 0) {
      patch.invoice_splits = [{ ...emptySplit(1), amount: fee }];
    }
    onUpdate(patch);
    setExpanded(v);
  };

  const generateWhole = async () => {
    try {
      const { data } = await api.post(`/deals/${deal.id}/generate-invoice`);
      toast.success(`draft invoice ${data.number} created`);
      await onGenerated?.();
      onNavigate?.(`/invoicing/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed'); }
  };
  const generateSplit = async (split_id) => {
    try {
      const { data } = await api.post(`/deals/${deal.id}/generate-invoice`, null, { params: { split_id } });
      toast.success(`draft invoice ${data.number} created`);
      await onGenerated?.();
      onNavigate?.(`/invoicing/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed'); }
  };
  // Delete the single raised invoice — resets the deal to blank so it can be left or generated again.
  const deleteWhole = async () => {
    if (!singleInv) return;
    if (!window.confirm(`Delete invoice ${singleInv.number}? This removes the raised invoice so you can leave it blank or generate it again.`)) return;
    try {
      await api.delete(`/invoices/${singleInv.id}`);
      await onUpdate?.({ invoice_status: 'to_invoice' });
      toast.success('invoice deleted');
      await onGenerated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to delete invoice'); }
  };
  // Delete a split's raised invoice and reset that split row.
  const deleteSplitInvoice = async (idx, split) => {
    if (!split.invoice_id) return;
    if (!window.confirm('Delete this split invoice? The split resets so you can generate it again.')) return;
    try {
      await api.delete(`/invoices/${split.invoice_id}`);
      setSplit(idx, { invoice_id: null, status: 'to_invoice' });
      toast.success('invoice deleted');
      await onGenerated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to delete invoice'); }
  };

  const splits = deal.invoice_splits || [];
  const splitsAmountTotal = splits.reduce((a, s) => a + (Number(s.amount) || 0), 0);
  const splitsComTotal = splits.reduce((a, s) => a + (Number(s.commission) || 0), 0);

  // Overdue if a generated invoice is past due & unpaid, or the manual status is overdue.
  const pastDue = (iso) => { if (!iso) return false; const d = new Date(iso + 'T00:00:00'); return !Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString()); };
  const isOverdue = (
    deal.invoice_status === 'overdue'
    || (singleInv && singleInv.status === 'overdue')
    || (singleInv && singleInv.status !== 'paid' && pastDue(singleInv.due_date))
    || (deal.split_invoicing && splits.some((s) => { const inv = dealInvoices.find((i) => i.id === s.invoice_id); return inv && inv.status !== 'paid' && (inv.status === 'overdue' || pastDue(inv.due_date)); }))
  );

  return (
    <>
      <tr className={`border-t border-slate-100 transition-colors ${isOverdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`} data-testid={`deal-invoicing-row-${deal.id}`}>
        <td className="px-2 py-3">
          {deal.split_invoicing && (
            <button onClick={() => setExpanded((v) => !v)} className="p-1 text-slate-400 hover:text-slate-700 rounded" data-testid={`expand-deal-${deal.id}`}>
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
        </td>
        <td className="px-3 py-3">
          <Link to={`/deals/${deal.id}`} className="font-medium text-slate-800 hover:text-pink-700 inline-flex items-center gap-1.5">
            {deal.title}
            <ExternalLink className="w-3 h-3 opacity-40" />
          </Link>
          <div className="text-xs text-slate-500 mt-0.5">{brand?.name || '—'}</div>
        </td>
        <td className="px-3 py-3 text-slate-900 font-medium">{fmtMoney(fee, deal.currency)}</td>
        <td className="px-3 py-3 text-slate-700">{fmtMoney(commission, deal.currency)}</td>
        <td className="px-3 py-3 text-slate-700">{fmtMoney(netFee, deal.currency)}</td>
        <td className="px-3 py-3">
          <Input
            value={deal.po_number || ''}
            onChange={(e) => onUpdate({ po_number: e.target.value })}
            className="h-8 text-sm w-28"
            placeholder="—"
            disabled={!canEdit}
            data-emoji-skip="true"
            data-testid={`po-number-${deal.id}`}
          />
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={Boolean(deal.split_invoicing)}
              onCheckedChange={toggleSplit}
              disabled={!canEdit}
              data-testid={`split-toggle-${deal.id}`}
            />
            {deal.split_invoicing && <span className="text-xs text-slate-500">{splits.length} split{splits.length === 1 ? '' : 's'}</span>}
          </div>
        </td>
        <td className="px-3 py-3">
          {deal.split_invoicing ? (
            <span className="text-xs text-slate-500">
              {splits.filter((s) => s.status === 'invoiced').length}/{splits.length} invoiced
            </span>
          ) : canEdit ? (
            <InvoiceStatusSelect deal={deal} value={invoicedAll && !isDealInvoiceStatusDone(deal.invoice_status) ? 'invoiced' : undefined} onUpdate={onUpdate} />
          ) : (
            <StatusChip status={invoicedAll ? 'invoiced' : (deal.invoice_status || 'to_invoice')} />
          )}
        </td>
        <td className="px-3 py-3 text-slate-600 text-xs" data-testid={`invoice-due-${deal.id}`}>
          {deal.split_invoicing
            ? '—'
            : singleInv?.due_date
              ? fmtDateFull(singleInv.due_date)
              : canEdit
                ? <Input type="date" value={deal.invoice_due_date || ''} onChange={(e) => onUpdate({ invoice_due_date: e.target.value })} className="h-8 text-xs w-36" data-testid={`manual-due-${deal.id}`} />
                : (deal.invoice_due_date ? fmtDateFull(deal.invoice_due_date) : <span className="text-slate-400 italic">not generated yet</span>)}
        </td>
        <td className="px-3 py-3">
          {canEdit && !deal.split_invoicing && !invoicedAll && (deal.invoice_status || 'to_invoice') === 'to_invoice' && (
            <Button size="sm" onClick={generateWhole} disabled={!canEdit || fee <= 0} className="h-7 px-2.5 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid={`generate-deal-${deal.id}`}>
              Generate
            </Button>
          )}
          {canEdit && !deal.split_invoicing && !invoicedAll && (deal.invoice_status || 'to_invoice') !== 'to_invoice' && (
            <span className="text-xs text-slate-400 italic">see status</span>
          )}
          {!deal.split_invoicing && invoicedAll && singleInv && (
            <span className="inline-flex items-center gap-2">
              <Link to={`/invoicing/${singleInv.id}`} className="text-xs text-pink-600 hover:text-pink-700 inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> {singleInv.number}
              </Link>
              {canEdit && (
                <button onClick={deleteWhole} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="delete raised invoice" data-testid={`delete-invoice-${deal.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          )}
          {deal.split_invoicing && <span className="text-xs text-slate-400 italic">see splits below</span>}
        </td>
        <td className="px-3 py-3 text-center">
          <PaymentCheck deal={deal} field="invoiced_at" color="#38bdf8" title="invoiced" canEdit={canEdit} onUpdate={onUpdate} />
        </td>
        <td className="px-3 py-3 text-center">
          <PaymentCheck deal={deal} field="paid_in_at" color="#a78bfa" title="paid in" canEdit={canEdit} onUpdate={onUpdate} />
        </td>
        <td className="px-3 py-3 text-center">
          <PaymentCheck deal={deal} field="paid_out_at" color="#059669" title="paid out" canEdit={canEdit} onUpdate={onUpdate} />
        </td>
        <td className="px-3 py-3 text-center">
          <InvoiceNotesCell deal={deal} talentMap={talentMap} canEdit={canEdit} onUpdate={onUpdate} />
        </td>
      </tr>

      {expanded && deal.split_invoicing && (
        <>
          <tr className="bg-lilac-50/40 border-t border-lilac-100 text-[10px] uppercase tracking-wide text-lilac-700">
            <th className="w-8"></th>
            <th className="px-3 py-1.5 text-left font-medium">Description</th>
            <th className="px-3 py-1.5 text-left font-medium">Amount</th>
            <th className="px-3 py-1.5 text-left font-medium">Invoice Date</th>
            <th className="px-3 py-1.5 text-left font-medium">Deal Invoice</th>
            <th className="px-3 py-1.5 text-left font-medium">Invoice Sent</th>
            <th className="px-3 py-1.5 text-left font-medium">Terms</th>
            <th className="px-3 py-1.5 text-left font-medium">Invoice Due</th>
            <th className="px-3 py-1.5 text-left font-medium">Invoice Status</th>
            <th className="w-8"></th>
          </tr>
          {splits.map((split, idx) => {
            const linkedInv = split.invoice_id ? dealInvoices.find((i) => i.id === split.invoice_id) : null;
            return (
              <DealSplitRow
                key={split.id}
                deal={deal}
                split={split}
                invoice={linkedInv}
                canEdit={canEdit}
                onPatch={(patch) => setSplit(idx, patch)}
                onRemove={() => removeSplit(idx)}
                onGenerate={() => generateSplit(split.id)}
                onDeleteInvoice={() => deleteSplitInvoice(idx, split)}
              />
            );
          })}
          <tr className="bg-lilac-50/30 border-t border-lilac-100">
            <td colSpan={2} className="px-3 py-2">
              {canEdit && !hideAddSplit && (
                <Button size="sm" variant="ghost" onClick={addSplit} className="h-8 border border-dashed border-lilac-300 text-lilac-700 hover:bg-lilac-100/50 text-xs" data-testid={`add-split-${deal.id}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> add invoice
                </Button>
              )}
            </td>
            <td className="px-3 py-2 text-xs font-medium text-slate-700">
              Total: {fmtMoney(splitsAmountTotal, deal.currency)}
              {Math.abs(splitsAmountTotal - fee) > 0.01 && <span className="ml-1.5 text-amber-600">(fee: {fmtMoney(fee, deal.currency)})</span>}
            </td>
            <td colSpan={7}></td>
          </tr>
        </>
      )}
    </>
  );
};

const DealsInvoicingTable = ({ q, statusFilter, talentId = 'all', canEdit = true }) => {
  const nav = useNavigate();
  const { deals, brandMap, updateDeal, loading: dealsLoading, refresh: refreshDeals } = useDeals();
  const { invoices, talentMap, refresh: refreshInvoices } = useInvoices();

  const filteredDeals = useMemo(() => {
    return deals.filter((d) => {
      if ((Number(d.value) || 0) <= 0) return false;
      if (!d.ready_to_invoice) return false;   // only show deals ticked as ready in the deals list
      if (talentId && talentId !== 'all' && !dealTalentIds(d).includes(talentId)) return false;
      if (q?.trim()) {
        const s = q.toLowerCase();
        const b = brandMap[d.brand_id];
        const tNames = dealTalentIds(d).map((id) => talentMap?.[id]?.name || '').join(' ');
        if (!`${d.title || ''} ${b?.name || ''} ${tNames}`.toLowerCase().includes(s)) return false;
      }
      if (statusFilter && statusFilter !== 'all') {
        const dealInvs = invoices.filter((i) => i.deal_id === d.id);
        const splits = d.invoice_splits || [];
        const isInvoiced = d.split_invoicing
          ? (splits.length > 0 && splits.every((s) => s.status === 'invoiced'))
          : (dealInvs.length > 0 || isDealInvoiceStatusDone(d.invoice_status));
        const isPaid = dealInvs.some((i) => i.status === 'paid') || d.invoice_status === 'paid_out';
        if (statusFilter === 'paid' && !isPaid) return false;
        if (statusFilter === 'invoiced' && (!isInvoiced || isPaid)) return false;
        if (statusFilter === 'to_invoice' && (isInvoiced || isPaid)) return false;
      }
      return true;
    }).sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));   // order deals as they go in (oldest first)
  }, [deals, brandMap, talentMap, q, statusFilter, talentId, invoices]);

  const updateAndReload = async (deal, patch) => {
    if (Object.keys(patch).length === 0) {
      await Promise.all([refreshDeals(), refreshInvoices()]);
      return;
    }
    await updateDeal(deal.id, patch);
  };

  const afterGenerate = async () => {
    await Promise.all([refreshDeals(), refreshInvoices()]);
  };

  const groupDeals = (list) => {
    const map = new Map();
    list.forEach((d) => {
      const tid = dealTalentIds(d)[0] || '__none__';
      if (!map.has(tid)) map.set(tid, []);
      map.get(tid).push(d);
    });
    return [...map.entries()]
      .map(([tid, ds]) => ({ talent: talentMap[tid] || null, deals: ds }))
      .sort((a, b) => (a.talent?.name || 'zzz').localeCompare(b.talent?.name || 'zzz'));
  };

  const allGroups = useMemo(() => groupDeals(filteredDeals), [filteredDeals, talentMap]);

  const renderGroup = ({ talent: t, deals: gdeals }) => {
    const gt = gdeals.reduce((acc, d) => {
      const f = Number(d.value) || 0;
      const c = f * (Number(d.commission_percent) || 0) / 100;
      acc.fee += f; acc.commission += c; acc.net += (f - c);
      return acc;
    }, { fee: 0, commission: 0, net: 0 });
    return (
      <div key={t?.user_id || '__none__'} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-pink-200" style={{ background: '#d394ab' }}>
          {t ? <InitialsAvatar name={t.name} size={32} /> : <div className="w-8 h-8 rounded-full bg-white/30 border border-dashed border-white/50" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white truncate">{t?.name || 'unassigned'}</div>
            <div className="text-xs text-white/80">{gdeals.length} deal{gdeals.length === 1 ? '' : 's'}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-white/80">fees</div>
            <div className="text-sm font-semibold text-white">{fmtMoney(gt.fee)}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <InvHead />
            <tbody>
              {gdeals.map((deal) => (
                <DealInvoicingRow
                  key={deal.id}
                  deal={deal}
                  invoices={invoices}
                  brandMap={brandMap}
                  talentMap={talentMap}
                  canEdit={canEdit}
                  onUpdate={(patch) => updateAndReload(deal, patch)}
                  onGenerated={afterGenerate}
                  onNavigate={(url) => nav(url)}
                />
              ))}
            </tbody>
            <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-slate-800 font-semibold" data-testid={`inv-totals-${t?.user_id || 'none'}`}>
              <tr>
                <td className="px-2 py-3"></td>
                <td className="px-3 py-3 text-right text-[11px] uppercase tracking-widest text-slate-500">totals ({gdeals.length})</td>
                <td className="px-3 py-3">{fmtMoney(gt.fee)}</td>
                <td className="px-3 py-3">{fmtMoney(gt.commission)}</td>
                <td className="px-3 py-3">{fmtMoney(gt.net)}</td>
                <td className="px-3 py-3" colSpan={9}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  if (dealsLoading) {
    return <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm" data-testid="deals-invoicing-table">loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="deals-invoicing-table">
      {allGroups.length > 0
        ? allGroups.map(renderGroup)
        : <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-400">no deals ticked ready to invoice — tick “ready to invoice” on a deal to see it here</div>}
    </div>
  );
};

export default DealsInvoicingTable;
